import React from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseButton from './Common/CloseButton';
import { getTriggerVisualConfig } from '../Utils/workflowTriggerVisuals';

const MULTI_SELECT_MENU_PROPS = {
  PaperProps: {
    sx: {
      maxHeight: 360,
    }
  }
};

const TRIGGER_MODE_MENU_PROPS = {
  PaperProps: {
    sx: {
      mt: 0.75,
      maxHeight: 'min(480px, calc(100dvh - 80px))',
      maxWidth: 'calc(100vw - 16px)',
      borderRadius: 2.5,
      border: '1px solid',
      borderColor: 'divider',
      boxShadow: '0 14px 38px rgba(15, 23, 42, 0.16)',
    }
  },
  MenuListProps: {
    sx: { p: 0.75 }
  }
};

const TRIGGER_MODE_OPTION_DEFINITIONS = [
  {
    value: 'disabled',
    labelKey: 'workflow.automation_mode_disabled',
    labelFallback: 'Manual only',
    helpFallback: 'No automatic runs. Start this flow manually or from another flow.',
    tooltipFallback: 'No event, detail change, confirmation, interval, or calendar schedule starts this flow. Manual replay and the Trigger Flow action can still start it.'
  },
  {
    value: 'event',
    labelKey: 'workflow.automation_mode_event',
    labelFallback: 'New or updated event',
    helpFallback: 'Run when a document or event is processed.',
    tooltipFallback: 'Starts in the normal event-driven processing path when a document or event is processed. It does not wait for a detail-change filter or a schedule.'
  },
  {
    value: 'event_confirmed',
    labelKey: 'workflow.automation_mode_event_confirmed',
    labelFallback: 'When event is confirmed',
    helpFallback: 'Run only when an event changes from not confirmed to confirmed.',
    tooltipFallback: 'Starts only when an event transitions from not confirmed to confirmed. Unconfirming an event does not match this trigger.'
  },
  {
    value: 'event_change',
    labelKey: 'workflow.automation_mode_event_change',
    labelFallback: 'On event detail change',
    helpFallback: 'Run when an event detail matches the filters configured below.',
    tooltipFallback: 'Starts only when an explicit confirmation, tag, event date, or ingestion change matches every filter configured below. Empty filters accept any detailed change.'
  },
  {
    value: 'time',
    labelKey: 'workflow.automation_mode_time',
    labelFallback: 'Recurring interval',
    helpFallback: 'Run repeatedly every chosen number of minutes, hours, or days.',
    tooltipFallback: 'The scheduler starts this flow at the interval configured below. Event and document updates do not start it; actual start time follows the scheduler polling cycle.'
  },
  {
    value: 'calendar',
    labelKey: 'workflow.automation_mode_calendar',
    labelFallback: 'Calendar schedule',
    helpFallback: 'Run when the selected calendar fields match.',
    tooltipFallback: 'The scheduler starts this flow when all configured weekday, day-of-month, month, hour, and minute fields match in the saved time zone. Empty fields match any value.'
  }
];

const getTriggerModeOptions = (t) => TRIGGER_MODE_OPTION_DEFINITIONS.map((option) => ({
  ...option,
  label: t(option.labelKey, option.labelFallback),
  description: t(`workflow.automation_mode_help_${option.value}`, option.helpFallback),
  tooltip: t(`workflow.automation_mode_tooltip_${option.value}`, option.tooltipFallback),
}));

const TriggerModeOptionContent = ({ option, showTooltip = false, tooltipLabel }) => {
  const visualConfig = getTriggerVisualConfig(option.value);
  const TriggerIcon = visualConfig.icon;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: { xs: 0.75, sm: 1.25 },
        width: '100%',
        minWidth: 0
      }}
    >
      <Box
        sx={{
          width: showTooltip ? { xs: 36, sm: 42 } : { xs: 34, sm: 38 },
          height: showTooltip ? { xs: 36, sm: 42 } : { xs: 34, sm: 38 },
          borderRadius: { xs: 1.5, sm: 2 },
          flex: '0 0 auto',
          display: 'grid',
          placeItems: 'center',
          color: visualConfig.iconColor,
          background: visualConfig.iconBackground,
          border: '1px solid',
          borderColor: visualConfig.borderColor,
        }}
      >
        <TriggerIcon fontSize="small" />
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="subtitle2" sx={{ color: 'text.primary', fontWeight: 700, lineHeight: 1.3 }}>
          {option.label}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', lineHeight: 1.35, whiteSpace: 'normal', mt: 0.2 }}
        >
          {option.description}
        </Typography>
      </Box>

      {showTooltip && (
        <Tooltip
          arrow
          placement="top"
          enterTouchDelay={0}
          leaveTouchDelay={4000}
          title={(
            <Box sx={{ maxWidth: 300, p: 0.25 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                {option.label}
              </Typography>
              <Typography variant="body2" sx={{ lineHeight: 1.45 }}>
                {option.tooltip}
              </Typography>
            </Box>
          )}
        >
          <IconButton
            size="small"
            aria-label={`${tooltipLabel}: ${option.label}`}
            onMouseDown={(event) => event.stopPropagation()}
            onClick={(event) => event.stopPropagation()}
            sx={{
              flex: '0 0 auto',
              p: { xs: 0.5, sm: 1 },
              color: visualConfig.iconColor,
              bgcolor: visualConfig.labelBackground,
              '&:hover': { bgcolor: visualConfig.iconBackground }
            }}
          >
            <InfoOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};

const EVENT_CHANGE_FILTER_DEFAULT = {
  change_type: 'any',
  operation: 'any',
  tag_variable: '',
  value_type: ''
};

const EVENT_CHANGE_OPERATIONS = {
  any: ['any', 'created', 'updated', 'deleted', 'processed'],
  confirm: ['updated'],
  tag: ['any', 'created', 'updated', 'deleted'],
  date: ['updated'],
  ingestion: ['processed'],
};

const getEventChangeOperations = (changeType) => (
  EVENT_CHANGE_OPERATIONS[changeType] || EVENT_CHANGE_OPERATIONS.any
);

const normalizeEventChangeFilter = (filter) => ({
  ...EVENT_CHANGE_FILTER_DEFAULT,
  ...(filter && typeof filter === 'object' && !Array.isArray(filter) ? filter : {})
});

const renderScheduleSelectValue = (selected, options, placeholder) => {
  const values = Array.isArray(selected) ? selected : [];
  if (!values.length) {
    return (
      <Box component="span" sx={{ color: 'text.secondary' }}>
        {placeholder}
      </Box>
    );
  }

  const optionMap = new Map((options || []).map((option) => [String(option.value), option.label]));
  const labels = values
    .map((value) => optionMap.get(String(value)) ?? String(value))
    .filter(Boolean);

  if (!labels.length) {
    return (
      <Box component="span" sx={{ color: 'text.secondary' }}>
        {placeholder}
      </Box>
    );
  }

  return (
    <Box component="span" sx={{ whiteSpace: 'normal' }}>
      {labels.join(', ')}
    </Box>
  );
};

const ScheduleMultiSelect = ({
  id,
  label,
  value,
  onChange,
  options,
  placeholder,
  sx,
}) => {
  const selectedValues = Array.isArray(value) ? value : [];
  const selectedValueSet = new Set(selectedValues.map((item) => String(item)));

  return (
    <FormControl fullWidth size="small" sx={sx}>
      <InputLabel id={`${id}-label`} shrink>
        {label}
      </InputLabel>
      <Select
        labelId={`${id}-label`}
        multiple
        displayEmpty
        value={value}
        label={label}
        onChange={(event) => onChange(event.target.value)}
        renderValue={(selected) => renderScheduleSelectValue(selected, options, placeholder)}
        MenuProps={MULTI_SELECT_MENU_PROPS}
        sx={{
          '& .MuiSelect-select': {
            whiteSpace: 'normal',
          }
        }}
      >
        {(options || []).map((option) => (
          <MenuItem key={option.value} value={option.value}>
            <Checkbox checked={selectedValueSet.has(String(option.value))} />
            <ListItemText primary={option.label} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

const WorkflowFlowAutomationDialog = ({
  open,
  mode,
  flowName,
  triggerMode,
  triggerFilter = EVENT_CHANGE_FILTER_DEFAULT,
  frequencyValue,
  frequencyUnit,
  scheduleDayOfWeek,
  scheduleDayOfMonth,
  scheduleMonth,
  scheduleHour,
  scheduleMinute,
  scheduleTimezone,
  calendarWeekdayOptions,
  calendarDayOfMonthOptions,
  calendarMonthOptions,
  calendarHourOptions,
  calendarMinuteOptions,
  error,
  isSubmitting = false,
  onClose,
  onSubmit,
  onFlowNameChange,
  onTriggerModeChange,
  onTriggerFilterChange,
  onFrequencyValueChange,
  onFrequencyUnitChange,
  onScheduleDayOfWeekChange,
  onScheduleDayOfMonthChange,
  onScheduleMonthChange,
  onScheduleHourChange,
  onScheduleMinuteChange,
  t,
}) => {
  const eventChangeFilter = normalizeEventChangeFilter(triggerFilter);
  const updateEventChangeFilter = (updates) => {
    if (!onTriggerFilterChange) {
      return;
    }
    onTriggerFilterChange({
      ...eventChangeFilter,
      ...updates,
    });
  };
  const availableOperations = getEventChangeOperations(eventChangeFilter.change_type);
  const selectedOperation = availableOperations.includes(eventChangeFilter.operation)
    ? eventChangeFilter.operation
    : availableOperations[0];
  const eventChangeTooltipKey = `workflow.automation_event_change_filter_tooltip_${eventChangeFilter.change_type || 'any'}`;
  const eventChangeTooltipFallback = eventChangeFilter.change_type === 'confirm'
    ? 'Confirmation is triggered when an event is confirmed or unconfirmed. It is always an update.'
    : eventChangeFilter.change_type === 'date'
      ? 'Event date is triggered when an existing event date is changed. It is always an update.'
      : eventChangeFilter.change_type === 'tag'
        ? 'Tags can be created, updated, or deleted on an event.'
        : eventChangeFilter.change_type === 'ingestion'
          ? 'Ingestion is triggered when an event has been processed.'
          : 'Choose a change type and, optionally, an operation to control when this flow runs.';
  const triggerModeOptions = getTriggerModeOptions(t);
  const selectedTriggerModeOption = triggerModeOptions.find((option) => option.value === triggerMode)
    || triggerModeOptions[0];
  const triggerModeTooltipLabel = t('workflow.automation_mode_tooltip_label', 'Trigger details');
  const handleTriggerModeSelect = (event) => {
    const nextTriggerMode = event.target.value;
    const nextTriggerModeOption = triggerModeOptions.find(
      (option) => option.value === nextTriggerMode
    );
    const normalizedFlowName = String(flowName || '').trim();
    const genericDefaultName = t('workflow.new_flow_default', 'New Flow');
    const hasAutomaticFlowName = !normalizedFlowName
      || normalizedFlowName === genericDefaultName
      || triggerModeOptions.some((option) => option.label === normalizedFlowName);

    onTriggerModeChange(nextTriggerMode);
    if (
      mode === 'create'
      && hasAutomaticFlowName
      && nextTriggerModeOption
      && onFlowNameChange
    ) {
      onFlowNameChange(nextTriggerModeOption.label);
    }
  };

  return (
  <Dialog
    open={open}
    onClose={isSubmitting ? undefined : onClose}
    maxWidth="sm"
    fullWidth
    PaperProps={{
      sx: {
        m: { xs: 0, sm: 4 },
        width: { xs: '100%', sm: 'calc(100% - 64px)' },
        height: { xs: '100dvh', sm: 'auto' },
        maxHeight: { xs: '100dvh', sm: 'calc(100% - 64px)' },
        borderRadius: { xs: 0, sm: 3 },
        boxShadow: 10
      }
    }}
  >
    <DialogTitle sx={{
      bgcolor: 'primary.main',
      color: 'white',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      pr: 1,
      py: { xs: 1.5, sm: 2 },
      fontSize: { xs: '1.1rem', sm: '1.25rem' }
    }}>
      {mode === 'create'
        ? t('workflow.automation_dialog_create_title', 'Create Flow')
        : t('workflow.automation_dialog_edit_title', 'Flow Automation')}
      <CloseButton
        ariaLabel="close"
        onClick={onClose}
        disabled={isSubmitting}
        sx={{ color: 'white' }}
      />
    </DialogTitle>
    <DialogContent sx={{ pt: 2, px: { xs: 2, sm: 3 } }}>
      {mode === 'create' && (
        <TextField
          fullWidth
          size="small"
          margin="normal"
          label={t('workflow.automation_flow_name_label', 'Flow name')}
          value={flowName}
          onChange={(event) => onFlowNameChange(event.target.value)}
        />
      )}

      <FormControl fullWidth sx={{ mt: 1, mb: 2 }}>
        <InputLabel id="flow-automation-mode-label">
          {t('workflow.automation_mode_label', 'Trigger mode')}
        </InputLabel>
        <Select
          labelId="flow-automation-mode-label"
          value={triggerMode}
          label={t('workflow.automation_mode_label', 'Trigger mode')}
          onChange={handleTriggerModeSelect}
          renderValue={() => (
            <TriggerModeOptionContent option={selectedTriggerModeOption} />
          )}
          MenuProps={TRIGGER_MODE_MENU_PROPS}
          sx={{
            borderRadius: 2,
            '& .MuiSelect-select': {
              py: 1.15,
              pr: '44px !important',
            },
            '& .MuiSelect-select:focus': {
              bgcolor: 'transparent'
            }
          }}
        >
          {triggerModeOptions.map((option) => (
            <MenuItem
              key={option.value}
              value={option.value}
              sx={{
                minHeight: { xs: 58, sm: 66 },
                px: { xs: 0.75, sm: 1.25 },
                py: { xs: 0.75, sm: 1 },
                mb: 0.5,
                borderRadius: 2,
                border: '1px solid transparent',
                whiteSpace: 'normal',
                '&:last-of-type': { mb: 0 },
                '&.Mui-selected': {
                  bgcolor: 'action.selected',
                  borderColor: getTriggerVisualConfig(option.value).borderColor,
                },
                '&.Mui-selected:hover': { bgcolor: 'action.selected' }
              }}
            >
              <TriggerModeOptionContent
                option={option}
                showTooltip
                tooltipLabel={triggerModeTooltipLabel}
              />
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {triggerMode === 'event_change' && (
        <Box
          sx={{
            display: 'grid',
            gap: 1.5,
            mb: 1,
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }
          }}
        >
          <FormControl fullWidth size="small">
            <InputLabel id="flow-event-change-type-label">
              {t('workflow.automation_event_change_type_label', 'Change type')}
            </InputLabel>
            <Select
              labelId="flow-event-change-type-label"
              value={eventChangeFilter.change_type || 'any'}
              label={t('workflow.automation_event_change_type_label', 'Change type')}
              onChange={(event) => {
                const changeType = event.target.value || 'any';
                const updates = { change_type: changeType };
                const operations = getEventChangeOperations(changeType);
                if (!operations.includes(eventChangeFilter.operation)) {
                  updates.operation = operations[0];
                }
                if (changeType !== 'tag') {
                  updates.tag_variable = '';
                  updates.value_type = '';
                }
                updateEventChangeFilter(updates);
              }}
            >
              <MenuItem value="any">{t('workflow.automation_event_change_type_any', 'Any change')}</MenuItem>
              <MenuItem value="confirm">{t('workflow.automation_event_change_type_confirm', 'Confirmation')}</MenuItem>
              <MenuItem value="tag">{t('workflow.automation_event_change_type_tag', 'Tag')}</MenuItem>
              <MenuItem value="date">{t('workflow.automation_event_change_type_date', 'Event date')}</MenuItem>
              <MenuItem value="ingestion">{t('workflow.automation_event_change_type_ingestion', 'Ingestion')}</MenuItem>
            </Select>
          </FormControl>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="flow-event-change-operation-label">
                {t('workflow.automation_event_change_operation_label', 'Operation')}
              </InputLabel>
              <Select
                labelId="flow-event-change-operation-label"
                value={selectedOperation}
                label={t('workflow.automation_event_change_operation_label', 'Operation')}
                onChange={(event) => updateEventChangeFilter({ operation: event.target.value || 'any' })}
              >
                {availableOperations.map((operation) => (
                  <MenuItem key={operation} value={operation}>
                    {t(`workflow.automation_event_change_operation_${operation}`, operation)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Tooltip title={t(eventChangeTooltipKey, eventChangeTooltipFallback)}>
              <IconButton
                size="small"
                aria-label={t('workflow.automation_event_change_filter_tooltip_label', 'Explain this filter')}
              >
                <InfoOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          {eventChangeFilter.change_type === 'tag' && (
            <>
              <TextField
                fullWidth
                size="small"
                label={t('workflow.automation_event_change_tag_variable_label', 'Tag variable')}
                value={eventChangeFilter.tag_variable || ''}
                onChange={(event) => updateEventChangeFilter({ tag_variable: event.target.value })}
                placeholder={t('workflow.automation_event_change_tag_variable_placeholder', 'status')}
              />
              <TextField
                fullWidth
                size="small"
                label={t('workflow.automation_event_change_value_type_label', 'Value type')}
                value={eventChangeFilter.value_type || ''}
                onChange={(event) => updateEventChangeFilter({ value_type: event.target.value })}
                placeholder={t('workflow.automation_event_change_value_type_placeholder', 'text')}
              />
            </>
          )}

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ gridColumn: '1 / -1', display: 'block', mt: -0.25 }}
          >
            {t('workflow.automation_event_change_filter_help', 'Leave fields on Any or empty to match all event detail changes.')}
          </Typography>
        </Box>
      )}

      {triggerMode === 'time' && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 1.5,
            alignItems: { xs: 'stretch', sm: 'center' },
            mb: 1
          }}
        >
          <TextField
            type="number"
            size="small"
            label={t('workflow.automation_frequency_value_label', 'Every')}
            value={frequencyValue}
            onChange={(event) => onFrequencyValueChange(event.target.value)}
            inputProps={{ min: 1, step: 1 }}
            sx={{ flex: 1 }}
          />
          <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 130 } }}>
            <InputLabel id="flow-automation-unit-label">
              {t('workflow.automation_frequency_unit_label', 'Unit')}
            </InputLabel>
            <Select
              labelId="flow-automation-unit-label"
              value={frequencyUnit}
              label={t('workflow.automation_frequency_unit_label', 'Unit')}
              onChange={(event) => onFrequencyUnitChange(event.target.value)}
            >
              <MenuItem value="minutes">{t('workflow.automation_unit_minutes', 'minutes')}</MenuItem>
              <MenuItem value="hours">{t('workflow.automation_unit_hours', 'hours')}</MenuItem>
              <MenuItem value="days">{t('workflow.automation_unit_days', 'days')}</MenuItem>
            </Select>
          </FormControl>
        </Box>
      )}

      {triggerMode === 'calendar' && (
        <Box
          sx={{
            display: 'grid',
            gap: 1.5,
            mb: 1,
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ gridColumn: '1 / -1', display: 'block', mt: -0.25 }}
          >
            {t('workflow.automation_calendar_help', 'Leave a field empty to match any value.')}
          </Typography>

          <ScheduleMultiSelect
            id="flow-automation-weekdays"
            label={t('workflow.automation_weekdays_label', 'Weekdays')}
            value={scheduleDayOfWeek}
            onChange={onScheduleDayOfWeekChange}
            options={calendarWeekdayOptions}
            placeholder={t('workflow.automation_calendar_any_value', 'Any')}
            sx={{ gridColumn: '1 / -1' }}
          />

          <ScheduleMultiSelect
            id="flow-automation-days-of-month"
            label={t('workflow.automation_days_of_month_label', 'Days of month')}
            value={scheduleDayOfMonth}
            onChange={onScheduleDayOfMonthChange}
            options={calendarDayOfMonthOptions}
            placeholder={t('workflow.automation_calendar_any_value', 'Any')}
          />

          <ScheduleMultiSelect
            id="flow-automation-months"
            label={t('workflow.automation_months_label', 'Months')}
            value={scheduleMonth}
            onChange={onScheduleMonthChange}
            options={calendarMonthOptions}
            placeholder={t('workflow.automation_calendar_any_value', 'Any')}
          />

          <ScheduleMultiSelect
            id="flow-automation-hours"
            label={t('workflow.automation_hours_label', 'Hours')}
            value={scheduleHour}
            onChange={onScheduleHourChange}
            options={calendarHourOptions}
            placeholder={t('workflow.automation_calendar_any_value', 'Any')}
          />

          <ScheduleMultiSelect
            id="flow-automation-minutes"
            label={t('workflow.automation_minutes_label', 'Minutes')}
            value={scheduleMinute}
            onChange={onScheduleMinuteChange}
            options={calendarMinuteOptions}
            placeholder={t('workflow.automation_calendar_any_value', 'Any')}
          />

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ gridColumn: '1 / -1', display: 'block' }}
          >
            {t('workflow.automation_calendar_timezone_help', 'Times are saved in {{timezone}}. Empty fields match any value.', {
              timezone: scheduleTimezone || 'UTC'
            })}
          </Typography>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 1.5 }}>
          {error}
        </Alert>
      )}
    </DialogContent>
    <DialogActions
      sx={{
        p: { xs: 1.5, sm: 2 },
        gap: 1,
        justifyContent: 'space-between',
        bgcolor: 'background.paper',
        borderTop: { xs: '1px solid', sm: 'none' },
        borderColor: 'divider',
        '& .MuiButton-root': { flex: { xs: 1, sm: '0 0 auto' }, minWidth: 0 }
      }}
    >
      <Button onClick={onClose} variant="outlined" disabled={isSubmitting}>
        {t('common.cancel', 'Cancel')}
      </Button>
      <Button
        onClick={onSubmit}
        variant="contained"
        disabled={isSubmitting}
        startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
      >
        {isSubmitting
          ? t('workflow.automation_saving_submit', 'Saving...')
          : mode === 'create'
            ? t('workflow.automation_create_submit', 'Create Flow')
            : t('workflow.automation_save_submit', 'Save Automation')}
      </Button>
    </DialogActions>
  </Dialog>
  );
};

export default WorkflowFlowAutomationDialog;
